import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Box, ButtonBase, Typography } from '@mui/material';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';

import addNotification from '../../utils/addNotification';
import { NotificationType } from '../../config';
import { EmailReplyToLevel } from '../../config/email';
import { TemplateType } from '../../config/template';
import { isElectronicInvoiceDocument } from '../../utils/digitalSigning';
import {
  ButtonUI,
  CheckboxUI,
  DialogUI,
  FileUploadUI,
  FormLabelUI,
  InputUI,
  RichTextEditorUI,
  SelectUI,
} from '../Interface';

const templateVariables = [
  { token: '{{docNum}}', description: 'Bizonylatszám' },
  { token: '{{companyName}}', description: 'Partner neve' },
  { token: '{{DocDate}}', description: 'Dátum' },
  { token: '{{issueDate}}', description: 'Bizonylat dátuma' },
  { token: '{{dueDate}}', description: 'Esedékesség dátuma' },
  { token: '{{reference}}', description: 'Hivatkozási szám' },
  { token: '{{userName}}', description: 'Küldő felhasználó neve' },
  { token: '{{userEmail}}', description: 'Küldő felhasználó e-mail címe' },
];

const TemplateVariablePanel = ({ onInsertVariable }) => {
  return (
    <Box
      sx={{
        p: 2,
        border: '1px solid',
        borderColor: 'divider',
        borderRadius: 1,
        backgroundColor: 'grey.50',
      }}
    >
      <Typography variant="subtitle2" sx={{ color: 'text.primary', fontWeight: 600 }}>
        Sablonváltozók
      </Typography>
      <Typography variant="body2" sx={{ mt: 2, color: 'text.secondary' }}>
        A tárgy és a szöveg sablonváltozókat tartalmazhat.
      </Typography>
      <Typography variant="body2" sx={{ color: 'text.secondary' }}>
        Ezek küldéskor automatikusan behelyettesítésre kerülnek a bizonylat adataival.
      </Typography>
      <Typography variant="subtitle2" sx={{ mt: 3, color: 'text.primary', fontWeight: 600 }}>
        Elérhető változók
      </Typography>
      <Box
        sx={{
          mt: 1.5,
          display: 'grid',
          gap: 1,
          gridTemplateColumns: {
            xs: '1fr',
            md: 'repeat(2, minmax(0, 1fr))',
            lg: 'repeat(3, minmax(0, 1fr))',
          },
        }}
      >
        {templateVariables.map((variable) => (
          <ButtonBase
            key={variable.token}
            onMouseDown={(event) => {
              event.preventDefault();
              onInsertVariable(variable.token);
            }}
            sx={{
              display: 'block',
              width: '100%',
              px: 1.25,
              py: 1,
              textAlign: 'left',
              border: '1px solid',
              borderColor: 'divider',
              borderRadius: 1,
              backgroundColor: 'common.white',
              boxShadow: 1,
              alignItems: 'stretch',
              justifyContent: 'flex-start',
              '&:hover': {
                borderColor: 'primary.main',
                backgroundColor: 'grey.50',
              },
            }}
          >
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                gap: 1,
              }}
            >
              <Typography variant="body2" sx={{ color: 'text.primary', fontWeight: 600 }}>
                {variable.token}
              </Typography>
              <Typography variant="caption" sx={{ color: 'text.secondary', textAlign: 'right' }}>
                {variable.description}
              </Typography>
            </Box>
          </ButtonBase>
        ))}
      </Box>
    </Box>
  );
};

const getUserEmail = (user) => {
  return user?.email ?? user?.mail ?? user?.emailAddress ?? user?.userEmail ?? '';
};

const getSelectedContact = (selectedCompany, documentValues) => {
  const contacts = selectedCompany?.companyContact ?? [];

  return (
    contacts.find((contact) => Number(contact?.id) === Number(documentValues?.companyContactId)) ??
    contacts.find((contact) => contact?.default) ??
    contacts[0] ??
    null
  );
};

const getCompanyEmailRecipients = (selectedCompany, currentDocType) => {
  const documentTypeId = currentDocType?.documentTypeId ?? null;
  const companyEmails = selectedCompany?.companyEmail ?? [];

  const matchingEmails = companyEmails
    .filter((row) => Number(row?.documentTypeId) === Number(documentTypeId))
    .map((row) => row?.email?.trim())
    .filter(Boolean);

  if (matchingEmails.length === 0) return '';

  return matchingEmails.join('; ');
};

const getReplyToAddress = ({ config, currentDocType, user }) => {
  const centralReplyTo = config?.defaultReplyToAddress ?? config?.emailSenderAddress ?? '';
  const userReplyTo = getUserEmail(user);
  const documentTypeReplyTo = currentDocType?.defaultReplyToAddress ?? centralReplyTo;

  switch (currentDocType?.defaultReplyToLevel) {
    case EmailReplyToLevel.USER:
      return userReplyTo || centralReplyTo;
    case EmailReplyToLevel.DOCUMENT_TYPE:
      return documentTypeReplyTo || centralReplyTo;
    case EmailReplyToLevel.CENTRAL:
    default:
      return centralReplyTo;
  }
};

const getEmailFromAddress = (config, documentValues, currentDocType) => {
  const senderAddress = isElectronicInvoiceDocument(documentValues, currentDocType?.documentTypeId)
    ? config?.electronicInvoiceEmailAddress?.trim()
    : config?.emailSenderAddress?.trim();
  const senderName = config?.emailSenderName?.trim();

  if (!senderAddress) return null;
  if (!senderName) return senderAddress;

  return `"${senderName.replace(/(["\\])/g, '\\$1')}" <${senderAddress}>`;
};

const getEmailTemplates = (currentDocType, selectedPrintReport, documentValues) => {
  const templateType = isElectronicInvoiceDocument(documentValues, currentDocType?.documentTypeId)
    ? TemplateType.ELECTRONIC_INVOICE_EMAIL
    : TemplateType.EMAIL;
  const templates = (currentDocType?.template ?? []).filter((template) => template?.templateType === templateType);
  const selectedFormId = selectedPrintReport?.formid ?? null;

  const formMatchedTemplates = templates.filter((template) => Number(template?.formId) === Number(selectedFormId));

  if (formMatchedTemplates.length > 0) return formMatchedTemplates;

  const formIndependentTemplates = templates.filter((template) => template?.formId == null);

  if (formIndependentTemplates.length > 0) return formIndependentTemplates;

  return templates;
};

const resolveInitialTemplate = (templates, documentValues) => {
  const documentLanguageCode = documentValues?.langCode ?? null;

  return (
    templates.find((template) => template?.languageCode && template.languageCode === documentLanguageCode) ??
    templates.find((template) => !template?.languageCode) ??
    templates[0] ??
    null
  );
};

const getTemplateOptions = (templates) => {
  return templates.map((template) => ({
    value: template.id,
    label: [template.name, template.languageCode].filter(Boolean).join(' - '),
  }));
};

const normalizeEmailField = (value) => {
  if (!value) return null;

  const items = String(value)
    .split(/[;,]/)
    .map((item) => item.trim())
    .filter(Boolean);

  if (items.length === 0) return null;
  if (items.length === 1) return items[0];

  return items;
};

const createInitialState = ({ config, currentDocType, documentValues, selectedCompany, selectedPrintReport, user }) => {
  const selectedContact = getSelectedContact(selectedCompany, documentValues);
  const userEmail = getUserEmail(user);
  const emailTemplates = getEmailTemplates(currentDocType, selectedPrintReport, documentValues);
  const initialTemplate = resolveInitialTemplate(emailTemplates, documentValues);
  const companyEmailRecipients = isElectronicInvoiceDocument(documentValues, currentDocType?.documentTypeId)
    ? documentValues.electronicInvoiceEmail ||
      selectedCompany?.electronicInvoiceEmail ||
      getCompanyEmailRecipients(selectedCompany, currentDocType)
    : getCompanyEmailRecipients(selectedCompany, currentDocType);
  const replyToAddress = getReplyToAddress({ config, currentDocType, user });

  return {
    selectedTemplateId: initialTemplate?.id ?? null,
    to: companyEmailRecipients || selectedContact?.email || selectedCompany?.email || '',
    cc: config?.emailSendCopyToSender ? userEmail : '',
    bcc: '',
    replyTo: replyToAddress,
    subject: initialTemplate?.subject ?? '',
    body: initialTemplate?.body ?? '',
    additionalAttachments: [],
    attachmentIds: [],
  };
};

const DocumentEmailDialog = ({
  config,
  currentDocType,
  documentValues,
  onClose,
  onSend,
  open,
  selectedCompany,
  selectedPrintReport,
  user,
}) => {
  const [formState, setFormState] = useState(() =>
    createInitialState({ config, currentDocType, documentValues, selectedCompany, selectedPrintReport, user })
  );
  const [uploadAreaKey, setUploadAreaKey] = useState(0);
  const [activeField, setActiveField] = useState('body');
  const subjectInputRef = useRef(null);
  const bodyEditorRef = useRef(null);
  const subjectSelectionRef = useRef({ start: 0, end: 0 });
  const emailTemplates = useMemo(
    () => getEmailTemplates(currentDocType, selectedPrintReport, documentValues),
    [currentDocType, selectedPrintReport, documentValues]
  );
  const templateOptions = useMemo(() => getTemplateOptions(emailTemplates), [emailTemplates]);
  const signedPdfAttachment = (documentValues?.attachment ?? []).find(
    (attachment) => attachment.isImmutablePdf || attachment.isDigitallySigned
  );
  const automaticHashFileName =
    documentValues?.electronicInvoiceMethod === 'NAV_HASH' &&
    config?.electronicInvoiceAutoSendHash &&
    signedPdfAttachment?.name
      ? `${signedPdfAttachment.name.replace(/\.pdf$/i, '')}.sha3-512.txt`
      : null;
  const documentAttachments = (documentValues?.attachment ?? []).filter(
    (attachment) =>
      Number.isInteger(attachment.id) &&
      attachment.deletedBy == null &&
      !attachment.deletedAt &&
      !attachment.external &&
      !attachment.isImmutablePdf &&
      !attachment.isDigitallySigned &&
      !attachment.isElectronicInvoiceHash
  );

  useEffect(() => {
    if (!open) return;

    setFormState(
      createInitialState({ config, currentDocType, documentValues, selectedCompany, selectedPrintReport, user })
    );
    setUploadAreaKey((prev) => prev + 1);
  }, [config, currentDocType, documentValues, open, selectedCompany, selectedPrintReport, user]);

  const syncSubjectSelection = () => {
    const input = subjectInputRef.current;

    if (!input) return;

    subjectSelectionRef.current = {
      start: input.selectionStart ?? 0,
      end: input.selectionEnd ?? input.selectionStart ?? 0,
    };
  };

  const handleFieldChange = (field) => (event) => {
    setFormState((prev) => ({
      ...prev,
      [field]: event?.target?.value ?? '',
    }));
  };

  const handleBodyChange = (value) => {
    setFormState((prev) => ({
      ...prev,
      body: value ?? '',
    }));
  };

  const handleFilesChange = (files) => {
    setFormState((prev) => ({
      ...prev,
      additionalAttachments: files ?? [],
    }));
  };

  const handleDocumentAttachmentChange = (attachmentId, checked) => {
    setFormState((prev) => {
      const attachmentIds = prev.attachmentIds.filter((id) => id !== attachmentId);
      return { ...prev, attachmentIds: checked ? [...attachmentIds, attachmentId] : attachmentIds };
    });
  };

  const handleTemplateChange = (event) => {
    const templateId = event?.target?.value ?? null;
    const selectedTemplate = emailTemplates.find((template) => Number(template.id) === Number(templateId)) ?? null;

    setFormState((prev) => ({
      ...prev,
      selectedTemplateId: templateId,
      subject: selectedTemplate?.subject ?? '',
      body: selectedTemplate?.body ?? '',
    }));
  };

  const insertIntoSubject = (token) => {
    const input = subjectInputRef.current;
    const currentValue = formState.subject ?? '';
    const insertion = `${token} `;

    if (!input) {
      setFormState((prev) => ({
        ...prev,
        subject: `${currentValue}${insertion}`,
      }));
      return;
    }

    const start = subjectSelectionRef.current.start ?? 0;
    const end = subjectSelectionRef.current.end ?? start;
    const nextValue = currentValue.slice(0, start) + insertion + currentValue.slice(end);
    const nextPosition = start + insertion.length;

    setFormState((prev) => ({
      ...prev,
      subject: nextValue,
    }));
    subjectSelectionRef.current = { start: nextPosition, end: nextPosition };

    requestAnimationFrame(() => {
      input.focus();
      input.setSelectionRange(nextPosition, nextPosition);
    });
  };

  const insertIntoBody = (token) => {
    bodyEditorRef.current?.insertText?.(`${token} `);
  };

  const handleInsertVariable = (token) => {
    if (activeField === 'subject') {
      insertIntoSubject(token);
      return;
    }

    insertIntoBody(token);
  };

  const handleSend = async () => {
    if (!formState.to?.trim()) {
      addNotification({
        content: 'A címzett megadása kötelező.',
        type: NotificationType.WARNING,
      });
      return;
    }

    if (!formState.body?.trim()) {
      addNotification({
        content: 'Az e-mail szövege nem lehet üres.',
        type: NotificationType.WARNING,
      });
      return;
    }

    if (!onSend) {
      addNotification({
        content: 'Az e-mail küldés backend bekötése még hiányzik.',
        type: NotificationType.INFO,
      });
      return;
    }

    await onSend({
      emailTo: normalizeEmailField(formState.to),
      emailCc: normalizeEmailField(formState.cc),
      emailBcc: normalizeEmailField(formState.bcc),
      replyTo: normalizeEmailField(formState.replyTo),
      subject: formState.subject,
      body: formState.body,
      documentTypeId: currentDocType?.documentTypeId ?? null,
      formId: selectedPrintReport?.formid ?? null,
      baseId: documentValues?.docId ?? null,
      printReportId: selectedPrintReport?.id ?? null,
      userId: user?.userId ?? null,
      emailFrom: getEmailFromAddress(config, documentValues, currentDocType),
      files: (formState.additionalAttachments ?? []).map((attachment) => attachment.file).filter(Boolean),
      attachmentIds: formState.attachmentIds,
    });
  };

  return (
    <DialogUI
      open={open}
      onClose={onClose}
      headerContent="E-mail küldés indítása"
      maxWidth="lg"
      fullWidth
      closeBtn={false}
      sx={{
        '& .MuiDialog-container': {
          '& .MuiPaper-root': {
            width: '100%',
            maxWidth: '1100px',
          },
        },
      }}
    >
      <Box sx={{ pt: 2, display: 'flex', flexDirection: 'column', gap: 2.5 }}>
        {templateOptions.length > 0 && (
          <SelectUI
            value={formState.selectedTemplateId ?? ''}
            onChange={handleTemplateChange}
            option={templateOptions}
            label={<FormLabelUI text="Sablon" />}
          />
        )}

        <Box
          sx={{
            display: 'grid',
            gap: 2,
            gridTemplateColumns: {
              xs: '1fr',
              md: 'repeat(2, minmax(0, 1fr))',
            },
          }}
        >
          <InputUI
            value={formState.to}
            onChange={handleFieldChange('to')}
            label={<FormLabelUI text="Címzett" />}
            variant="standard"
            InputLabelProps={{ shrink: Boolean(formState.to) }}
          />
          <InputUI
            value={formState.replyTo}
            onChange={handleFieldChange('replyTo')}
            label={<FormLabelUI text="Válaszcím" />}
            variant="standard"
            InputLabelProps={{ shrink: Boolean(formState.replyTo) }}
          />
          <InputUI
            value={formState.cc}
            onChange={handleFieldChange('cc')}
            label={<FormLabelUI text="Másolat" />}
            variant="standard"
            InputLabelProps={{ shrink: Boolean(formState.cc) }}
          />
          <InputUI
            value={formState.bcc}
            onChange={handleFieldChange('bcc')}
            label={<FormLabelUI text="Titkos másolat" />}
            variant="standard"
            InputLabelProps={{ shrink: Boolean(formState.bcc) }}
          />
        </Box>

        <InputUI
          inputRef={subjectInputRef}
          value={formState.subject}
          onChange={handleFieldChange('subject')}
          onFocus={() => {
            setActiveField('subject');
            syncSubjectSelection();
          }}
          onClick={syncSubjectSelection}
          onKeyUp={syncSubjectSelection}
          onSelect={syncSubjectSelection}
          label={<FormLabelUI text="Tárgy" />}
          variant="standard"
          InputLabelProps={{ shrink: Boolean(formState.subject) }}
        />

        <RichTextEditorUI
          ref={bodyEditorRef}
          label={<FormLabelUI text="Szöveg" />}
          value={formState.body}
          onChange={handleBodyChange}
          onFocus={() => {
            setActiveField('body');
          }}
          minHeight={340}
        />

        <TemplateVariablePanel onInsertVariable={handleInsertVariable} />

        <Box
          sx={{
            p: 2,
            border: '1px solid',
            borderColor: 'divider',
            borderRadius: 1,
            backgroundColor: 'common.white',
          }}
        >
          <Typography variant="body2" sx={{ fontWeight: 600, color: 'text.primary' }}>
            Csatolmányok
          </Typography>
          {documentValues?.electronicSignatureRequested && (
            <Box sx={{ mt: 1, p: 1, display: 'flex', alignItems: 'center', gap: 1, bgcolor: 'grey.50', borderRadius: 1 }}>
              <PictureAsPdfIcon color="error" fontSize="small" />
              <Box sx={{ minWidth: 0 }}>
                <Typography variant="body2" sx={{ overflowWrap: 'anywhere' }}>
                  {signedPdfAttachment?.name ?? 'Az e-számla PDF nem érhető el.'}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  {documentValues?.electronicInvoiceMethod === 'NAV_HASH'
                    ? 'Automatikusan csatolt e-számla PDF'
                    : 'Automatikusan csatolt aláírt PDF'}
                </Typography>
              </Box>
            </Box>
          )}

          {automaticHashFileName && (
            <Box sx={{ mt: 1, p: 1, bgcolor: 'grey.50', borderRadius: 1 }}>
              <Typography variant="body2" sx={{ overflowWrap: 'anywhere' }}>
                {automaticHashFileName}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                Automatikusan csatolt hashfájl
              </Typography>
            </Box>
          )}

          <Box sx={{ mt: 1.5 }}>
            <FileUploadUI key={uploadAreaKey} onFilesChange={handleFilesChange} />
          </Box>

          <Typography variant="caption" sx={{ mt: 1, display: 'block', color: 'text.secondary' }}>
            Kiválasztott plusz csatolmányok: {formState.additionalAttachments.length}
          </Typography>
          {documentAttachments.length > 0 && (
            <Box sx={{ mt: 2, display: 'flex', flexDirection: 'column', alignItems: 'flex-start' }}>
              <Typography variant="body2" sx={{ fontWeight: 600 }}>
                Bizonylathoz mentett csatolmányok
              </Typography>
              <Typography variant="caption" color="text.secondary">
                Jelölje be az e-mailhez csatolandó fájlokat.
              </Typography>
              {documentAttachments.map((attachment) => (
                <CheckboxUI
                  key={attachment.id}
                  label={attachment.name}
                  checked={formState.attachmentIds.includes(attachment.id)}
                  onCheckedChange={(checked) => handleDocumentAttachmentChange(attachment.id, checked)}
                />
              ))}
            </Box>
          )}
        </Box>

        <Box display="flex" gap={2} justifyContent="center" pt={1}>
          <ButtonUI text="Küldés" color="green" type="button" onClick={handleSend} />
          <ButtonUI text="Mégsem" color="blue" type="button" onClick={onClose} />
        </Box>
      </Box>
    </DialogUI>
  );
};

export default DocumentEmailDialog;
